import { useMemo, useState } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button, buttonVariants } from '@cocoonspace/ui/components/button'
import { HeaderWithSeparator } from '@cocoonspace/ui/components/header-with-separator'
import { inputVariants } from '@cocoonspace/ui/components/input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@cocoonspace/ui/components/popover'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { CalendarIcon, ChevronDownIcon, XIcon } from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'
import { usePathname } from 'next/navigation'

import { useLocale } from '~/hooks/use-locale.hook'
import { stripLocaleFromPath } from '~/i18n/routing'
import { useTracking } from '~/providers/tracking-provider'
import { useYearFilter } from './events-list'
import type { EventsByMonth } from './get-filtered-events-by-month.util'

const YearsSection = ({
	value,
	onChange,
}: {
	value?: number
	onChange: (value: number) => void
}) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')

	const yearsList = useMemo(() => {
		const years = [new Date().getFullYear()]

		if (pathname.includes('past')) {
			years.unshift(years[0] - 1)
		} else {
			years.push(years[0] + 1)
		}

		return years
	}, [pathname])

	return (
		<div className='flex flex-col gap-2'>
			<HeaderWithSeparator>
				<Trans>Filtrer par année</Trans>
			</HeaderWithSeparator>

			<div className='grid grid-cols-4 gap-4'>
				{yearsList.map((year) => (
					<Button
						className='rounded'
						key={year}
						onClick={() => onChange(year)}
						size='sm'
						variant={year === value ? 'default' : 'outline'}
					>
						{year}
					</Button>
				))}
			</div>
		</div>
	)
}

const MonthsSections = ({
	months,
	onClick,
}: {
	months: Month[]
	onClick: (month: Month) => void
}) => (
	<div className='flex flex-col gap-2'>
		<HeaderWithSeparator>
			<Trans>Aller à</Trans>
		</HeaderWithSeparator>

		<div className='grid grid-cols-12 gap-2'>
			{months.map((el) => (
				<div
					className='col-span-3'
					key={el.label}
				>
					<Link
						aria-disabled={el.disabled}
						className={buttonVariants({
							className: cn(
								'hover:aria-disabled:border-foreground hover:aria-disabled:text-foreground w-full rounded-md capitalize aria-disabled:opacity-20',
								{ 'pointer-events-none': el.disabled },
							),
							size: 'sm',
							variant: 'outline',
						})}
						href={`#${el.hash}`}
						onClick={() => {
							if (el.disabled) return
							onClick(el)
						}}
					>
						{el.label}
					</Link>
				</div>
			))}
		</div>
	</div>
)

interface Month {
	label: string
	hash: string
	disabled: boolean
}

const YearFilterPopoverTrigger = ({
	activeYear,
	onReset,
}: {
	activeYear: number | undefined
	onReset: VoidFunction
}) => {
	const { t } = useLingui()

	return (
		<div
			className={inputVariants({
				className: 'items-center gap-2',
				inputSize: 'sm',
				variant: 'noBorder',
			})}
		>
			<CalendarIcon className='text-primary' />

			<PopoverTrigger asChild>
				<button
					className='whitespace-nowrap'
					data-testid='yearFilter-btn'
					data-umami-event='myEvents-yearFilter-btn'
					type='button'
				>
					{activeYear || t`Filtrer par date`}
				</button>
			</PopoverTrigger>

			{activeYear ? (
				<button
					className='text-primary right-4'
					aria-label={t`Annuler`}
					onClick={(e) => {
						e.preventDefault()
						onReset()
					}}
				>
					<XIcon className='text-primary' />
				</button>
			) : (
				<ChevronDownIcon />
			)}
		</div>
	)
}

export const EventYearFilter = ({
	eventsByMonth,
}: {
	eventsByMonth?: EventsByMonth[]
}) => {
	const { track } = useTracking()
	const locale = useLocale()

	const [year, setYear] = useYearFilter()

	const [isOpen, setIsOpen] = useState(false)

	const monthsList = useMemo<Month[]>(() => {
		const activeYear = year ?? new Date().getFullYear()

		return Array.from({ length: 12 }).map((_, index) => {
			const month = dateTimeUtils(`${activeYear}-${index + 1}-1`).locale(locale)

			const hash = `${month.format('MMMM')}-${activeYear ?? new Date().getFullYear()}`

			return {
				disabled: !eventsByMonth?.filter(
					(el) => el.month.replace(' ', '-') === hash,
				).length,
				hash,
				label: month.format('MMM'),
			}
		})
	}, [eventsByMonth, locale, year])

	const handleMonthClick = (month: Month) => {
		track.click('myEvents-yearFilter-monthBtn', {
			month: month.label,
		})

		setIsOpen(false)
	}

	return (
		<Popover
			onOpenChange={setIsOpen}
			open={isOpen}
		>
			<YearFilterPopoverTrigger
				activeYear={year || undefined}
				onReset={() => setYear(null)}
			/>

			<PopoverContent
				className='max-w-[550px]'
				onCloseAutoFocus={(e) => e.preventDefault()}
				sideOffset={10}
			>
				<div className='flex flex-col gap-4'>
					<YearsSection
						onChange={(year) => setYear(year)}
						value={year || undefined}
					/>

					<MonthsSections
						months={monthsList}
						onClick={handleMonthClick}
					/>
				</div>
			</PopoverContent>
		</Popover>
	)
}
